import React from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper
} from '@mui/material';
import PortfolioSummary from './PortfolioSummary';
import './OrdersTable.css';
import { useMediaQuery } from '@mui/material';
import { EXCHANGE_FEE_RATES } from '../../constants/exchange';

const formatNumber = (num) => num.toLocaleString();
const formatUSDT = (num) => num.toLocaleString('ko-KR', {
    maximumFractionDigits: 4
});
const formatPercent = (num) => num.toFixed(3);
const formatKRW = (num) => {
    const value = Math.floor(num / 1000);
    return value.toLocaleString() + 'K';
};
const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const isMobile = useMediaQuery('(max-width:768px)');
    
    if (isMobile) {
        return date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    } else {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        return `${month}.${day} ${time}`;
    }
};

const getTradeType = (korea_order, future_order) => {
    const isKoreaBuy = korea_order.order_info.side === 'buy';
    const isFutureSell = future_order.order_info.side === 'sell';
    
    if (isKoreaBuy && isFutureSell) {
        return {
            type: 'entry',
            exchange: korea_order.order_info.exchange
        };
    } else {
        return {
            type: 'exit',
            exchange: korea_order.order_info.exchange
        };
    }
};

const getExecutionDiff = (target_price, executed_price, is_sell) => {
    const diff = ((executed_price - target_price) / target_price) * 100;
    // 매도의 경우 높게 팔수록 좋고, 매수의 경우 낮게 살수록 좋음
    // diff가 0이면 무조건 positive
    const isPositive = diff === 0 ? true : (is_sell ? diff > 0 : diff < 0);
    return {
        diff: Math.abs(diff),
        isPositive
    };
};

const OrderPairSummary = ({ order }) => {
    const { korea_order, future_order, pnl } = order;
    const { krw_change, usdt_change, krw_from_usdt, total, target_premium, executed_premium, index_premium, base_usdt } = pnl;
    const tradeInfo = getTradeType(korea_order, future_order);
    const futureExecutedPrice = future_order.execution_result.executed_quantity === 0 ? future_order.order_info.price : future_order.execution_result.executed_price;
    const executedAmount = korea_order.execution_result.executed_quantity * korea_order.execution_result.executed_price;
    const [expanded, setExpanded] = React.useState(false);
    const [visibleColumns, setVisibleColumns] = React.useState(8);

    React.useEffect(() => {
        const updateVisibleColumns = () => {
            const width = window.innerWidth;
            setVisibleColumns(width <= 768 ? 6 : 8);
        };
        updateVisibleColumns();
        window.addEventListener('resize', updateVisibleColumns);
        return () => window.removeEventListener('resize', updateVisibleColumns);
    }, []);

    // 한국/해외 각각의 체결 차이 계산
    const koreaExecution = getExecutionDiff(
        korea_order.order_info.target_price,
        korea_order.execution_result.executed_price,
        tradeInfo.type === 'exit'  // exit일 때는 한국 매도
    );
    const futureExecution = getExecutionDiff(
        future_order.order_info.target_price,
        futureExecutedPrice,
        tradeInfo.type === 'entry'  // entry일 때는 해외 매도
    );

    return (
        <>
            <TableRow hover onClick={() => setExpanded(!expanded)} className="order-row">
                <TableCell className="col-time">
                    <span className="time-cell">
                        {formatTime(korea_order.execution_result.executed_timestamp)}
                    </span>
                </TableCell>
                <TableCell className="col-direction">
                    <span className={`exchange-type ${tradeInfo.type}`}>
                        {tradeInfo.type}
                    </span>
                </TableCell>
                <TableCell className="col-price" align="right">
                    <div className="price-change">
                        <span className="price-value">{formatNumber(korea_order.order_info.target_price)}</span>
                        <span className="arrow">→</span>
                        <span className="price-value">{formatNumber(korea_order.execution_result.executed_price)}</span>
                        <span className={`execution-diff ${koreaExecution.isPositive ? 'positive' : 'negative'}`}>
                            ({koreaExecution.isPositive ? '+' : '-'}{formatPercent(koreaExecution.diff)}%))
                        </span>
                        <span className="unit-text">KRW</span>
                    </div>
                    <div className="price-change">
                        <span className="price-value">{formatUSDT(future_order.order_info.target_price)}</span>
                        <span className="arrow">→</span>
                        <span className="price-value">{formatUSDT(future_order.execution_result.executed_price)}</span>
                        <span className={`execution-diff ${futureExecution.isPositive ? 'positive' : 'negative'}`}>
                            ({futureExecution.isPositive ? '+' : '-'}{formatPercent(futureExecution.diff)}%))
                        </span>
                        <span className="unit-text">USDT</span>
                    </div>
                </TableCell>
                <TableCell className="col-premium" align="right">
                    <div className="premium-value">
                        {formatPercent(target_premium)}→ {formatPercent(executed_premium)}
                        <span className="premium-unit">%</span>
                        <span style={{ 
                            marginLeft: '4px',
                            color: (korea_order.order_info.side === 'buy' ? executed_premium < target_premium : executed_premium > target_premium) ? '#2e7d32' : '#d32f2f'
                        }}>
                            ({executed_premium === target_premium ? '' : 
                              (korea_order.order_info.side === 'buy' ? 
                                (executed_premium < target_premium ? '+' : '-') : 
                                (executed_premium > target_premium ? '+' : '-'))}
                            {formatPercent(Math.abs(executed_premium - target_premium))}%)
                        </span>
                    </div>
                    <div className="premium-value market-premium">
                        {'시장 프리미엄: '+formatPercent(index_premium)}
                        <span className="premium-unit">%</span>
                    </div>
                </TableCell>
                <TableCell className="col-quantity" align="right">
                    <span className="amount-value">{formatNumber(korea_order.execution_result.executed_quantity)}</span>
                </TableCell>
                <TableCell className="col-executed" align="right">
                    <span className="amount-value">
                        {formatKRW(executedAmount)}
                        <span className="unit-text">KRW</span>
                    </span>
                </TableCell>
                <TableCell className="col-pnl" align="center">
                    <div className="pnl-cell">
                        <div className="pnl-row">{formatNumber(Math.round(krw_change))} KRW</div>
                        <div className="pnl-row">{formatNumber(usdt_change)} USDT</div>
                        <div className="pnl-row">
                            ({formatNumber(base_usdt)}원 × {formatNumber(usdt_change)} = {formatNumber(Math.round(base_usdt * usdt_change))}원)
                        </div>
                        <div className={`pnl-row total-pnl ${total >= 0 ? 'positive' : 'negative'}`}>
                            {formatNumber(Math.round(total))}
                            <span className="unit-text">KRW</span>
                        </div>
                    </div>
                </TableCell>
                <TableCell className="col-price-compare hide-on-tablet" align="right">
                    <div>진입:{formatNumber(korea_order.execution_result.executed_price / futureExecutedPrice)}</div>
                    <div>시장:{korea_order.order_info.strategy_info ? formatNumber((1+ korea_order.order_info.strategy_info.base_kimp/100) * korea_order.order_info.exchange_rate) : 'base_kimp 없음'}</div>
                    <div>테더:{formatNumber(korea_order.order_info.korea_USDT)}</div>
                </TableCell>
                <TableCell className="col-status">
                    <span className={`status-tag ${korea_order.execution_result.status}`}>
                        {korea_order.execution_result.status}
                    </span>
                    <br />
                    <span className={`status-tag ${future_order.execution_result.status}`}>
                        {future_order.execution_result.status}
                    </span>
                </TableCell>
            </TableRow>
            {expanded && (
                <tr className="details-row">
                    <td colSpan={visibleColumns} className="details-cell">
                        <div className="order-details-content">
                            <div className="order-details-section">
                                <h4>국내 거래소 주문</h4>
                                <div className="order-summary">
                                    <div className="summary-row">
                                        <span className="label">거래소:</span>
                                        <span className="value">{korea_order?.order_info?.exchange}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">심볼:</span>
                                        <span className="value">{korea_order?.order_info?.symbol}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">USDT 가격:</span>
                                        <span className="value">{korea_order?.order_info?.korea_USDT} KRW</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">Base Kimp:</span>
                                        <span className="value">
                                            {korea_order?.order_info?.strategy_info?.base_kimp ? 
                                                formatPercent(korea_order?.order_info?.strategy_info?.base_kimp) + '%' : 
                                                '없음'}
                                        </span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">주문유형:</span>
                                        <span className="value">{korea_order?.order_info?.type}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">목표가격:</span>
                                        <span className="value">{korea_order?.order_info?.target_price?.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">체결가격:</span>
                                        <span className="value">{korea_order?.execution_result?.executed_price?.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">목표 수량:</span>
                                        <span className="value">{formatNumber(korea_order?.order_info?.quantity)}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">체결수량:</span>
                                        <span className="value">{formatNumber(korea_order?.execution_result?.executed_quantity)}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">상태:</span>
                                        <span className="value">{korea_order?.execution_result?.status}</span>
                                    </div>
                                </div>
                                <div className="raw-data">
                                    <pre>
                                        {JSON.stringify(korea_order, null, 2)}
                                    </pre>
                                </div>
                            </div>
                            <div className="order-details-section">
                                <h4>해외 거래소 주문</h4>
                                <div className="order-summary">
                                    <div className="summary-row">
                                        <span className="label">거래소:</span>
                                        <span className="value">{future_order?.order_info?.exchange}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">심볼:</span>
                                        <span className="value">{future_order?.order_info?.symbol}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">USDT 가격:</span>
                                        <span className="value">{future_order?.order_info?.korea_USDT} KRW</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">Base Kimp:</span>
                                        <span className="value">
                                            {future_order?.order_info?.strategy_info?.base_kimp ? 
                                                formatPercent(future_order?.order_info?.strategy_info?.base_kimp) + '%' : 
                                                '없음'}
                                        </span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">주문유형:</span>
                                        <span className="value">{future_order?.order_info?.type}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">목표가격:</span>
                                        <span className="value">{future_order?.order_info?.target_price?.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">체결가격:</span>
                                        <span className="value">{future_order?.execution_result?.executed_price?.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">수량:</span>
                                        <span className="value">{formatNumber(future_order?.order_info?.quantity)}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">체결수량:</span>
                                        <span className="value">{formatNumber(future_order?.execution_result?.executed_quantity)}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="label">상태:</span>
                                        <span className="value">{future_order?.execution_result?.status}</span>
                                    </div>
                                </div>
                                <div className="raw-data">
                                    <pre>
                                        {JSON.stringify(future_order, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const OrdersTable = ({ orders }) => {
    // 최신 순으로 정렬
    const sortedOrders = [...orders].sort((a, b) => {
        const aTime = a.korea_order?.order_info?.timestamp || 0;
        const bTime = b.korea_order?.order_info?.timestamp || 0;
        return bTime - aTime;
    });

    // 첫 번째 주문에서 공통 정보 추출
    const firstOrder = sortedOrders[0];
    const commonInfo = firstOrder ? {
        exchange: firstOrder.korea_order?.order_info?.exchange,
        symbol: firstOrder.korea_order?.order_info?.symbol,
        exchange_rate: firstOrder.korea_order?.order_info?.exchange_rate,
        korea_USDT: firstOrder.korea_order?.order_info?.korea_USDT,
        future_exchange: firstOrder.future_order?.order_info?.exchange
    } : null;

    // 포트폴리오 전체 수익 계산
    const totalPortfolioPnL = sortedOrders.reduce((total, order) => {
        return total + (order.pnl?.total || 0);
    }, 0);

    // 거래소별 매수/매도 금액과 수량 계산
    const portfolioStats = sortedOrders.reduce((stats, order) => {
        const { korea_order, future_order } = order;
        const koreaAmount = korea_order.execution_result.executed_quantity * korea_order.execution_result.executed_price;
        const futureExecutedPrice = future_order.execution_result.executed_quantity === 0 ? future_order.order_info.price : future_order.execution_result.executed_price;
        const futureAmount = korea_order.execution_result.executed_quantity * futureExecutedPrice;
        
        if (korea_order.order_info.side === 'buy') {
            stats.totalQuantity += korea_order.execution_result.executed_quantity;
            stats.korea.buy += koreaAmount;
            stats.future.sell += futureAmount;
        } else {
            stats.totalQuantity -= korea_order.execution_result.executed_quantity;
            stats.korea.sell += koreaAmount;
            stats.future.buy += futureAmount;
        }

        return stats;
    }, {
        korea: { buy: 0, sell: 0 },
        future: { buy: 0, sell: 0 },
        totalQuantity: 0
    });

    // 수수료율 설정
    const feeRates = EXCHANGE_FEE_RATES;

    // 거래소 정보 로깅
    const koreaExchange = firstOrder?.korea_order?.order_info?.exchange?.toUpperCase();
    const futureExchange = firstOrder?.future_order?.order_info?.exchange?.toUpperCase();


    // 거래소별 통계 계산
    const exchangeStats = {
        korea: {
            position: portfolioStats.korea.sell - portfolioStats.korea.buy,
            volume: portfolioStats.korea.buy + portfolioStats.korea.sell,
            fee: (portfolioStats.korea.buy + portfolioStats.korea.sell) * (feeRates[koreaExchange] || 0.0005),
            feeRate: feeRates[koreaExchange] || 0.0005
        },
        future: {
            position: portfolioStats.future.sell - portfolioStats.future.buy,
            volume: portfolioStats.future.buy + portfolioStats.future.sell,
            fee: (portfolioStats.future.buy + portfolioStats.future.sell) * (feeRates[futureExchange] || 0.0002),
            feeRate: feeRates[futureExchange] || 0.0002
        }
    };


    const columns = [
        { 
            field: 'time', 
            headerName: useMediaQuery('(max-width:768px)') ? '시간' : '주문시간',
            className: 'col-time',
            align: 'center'
        },
        {
            field: 'direction',
            headerName: '방향',
            className: 'col-direction',
            align: 'center'
        },
        { 
            field: 'price', 
            headerName: useMediaQuery('(max-width:768px)') ? '가격' : '가격 체결 목표->결과',
            className: 'col-price',
            align: 'center'
        },
        { 
            field: 'premium', 
            headerName: useMediaQuery('(max-width:768px)') ? '김프' : '김프 진입기준->결과',
            className: 'col-premium',
            align: 'center'
        },
        { 
            field: 'quantity', 
            headerName: useMediaQuery('(max-width:768px)') ? '수량' : '체결수량',
            className: 'col-quantity',
            align: 'center'
        },
        { 
            field: 'executed', 
            headerName: useMediaQuery('(max-width:768px)') ? '체결' : '체결원화',
            className: 'col-executed',
            align: 'center'
        },
        {
            field: 'pnl',
            headerName: 'PnL',
            className: 'col-pnl',
            align: 'center'
        },
        { 
            field: 'price-compare', 
            headerName: '테더 비교',
            className: 'col-price-compare hide-on-tablet',
            align: 'right'
        },
        { 
            field: 'status', 
            headerName: '상태',
            className: 'col-status',
            align: 'center'
        }
    ];

    return (
        <div>
            <PortfolioSummary 
                commonInfo={commonInfo}
                portfolioStats={portfolioStats}
                exchangeStats={exchangeStats}
                totalPortfolioPnL={totalPortfolioPnL}
            />
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                <Table size="small" className="orders-table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.field} className={column.className} align={column.align}>
                                    <span className="header-value">{column.headerName}</span>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedOrders.map(order => (
                            <OrderPairSummary 
                                key={order.orderId} 
                                order={order} 
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default OrdersTable;
